// @flow

import { observer } from "mobx-react";
import React from "react";
import Cookies from "js-cookie";
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import AppearanceStore from "app/stores/AppearanceStore";
import TimelineItemStore from "app/stores/TimelineItemStore";
import VideoStore from "app/stores/VideoStore";
import VideoEditorStore from "app/stores/VideoEditorStore";
import VideoPlayerStore from "app/stores/VideoPlayerStore";
import VideoShareStore from "app/stores/VideoShareStore";
import VideoTitleEditorStore from "app/stores/VideoTitleEditorStore";
import UserStore from "app/stores/UserStore";

import UserService from "app/services/UserService";

import TimelineItem from "app/models/TimelineItem";
import Video from "app/models/Video";

import ConfirmationModal from "app/components/ConfirmationModal/ConfirmationModal";
import WatchTour from "app/components/WatchTour/WatchTour";
import Logo from "app/components/Logo/Logo";

import RelatedTimelinesContainer from "app/containers/RelatedTimelinesContainer";
import ScalerContainer from "app/containers/ScalerContainer";
import TimelineBarsContainer from "app/containers/TimelineBarsContainer";
import TimelineCardSwitcherContainer from "app/containers/TimelineCardSwitcherContainer";
import TimelineContainer from "app/containers/TimelineContainer";
import TimelineWizardContainer from "app/containers/TimelineWizardContainer";
import VideoBeforePlayOverlayContainer from "app/containers/VideoBeforePlayOverlayContainer";
import VideoEndingContainer from "app/containers/VideoEndingContainer";
import VideoShareContainer from "app/containers/VideoShareContainer";
import VideoStatusContainer from "app/containers/VideoStatusContainer";
import VideoPlayerContainer from "app/containers/VideoPlayerContainer";
import VideoProgressContainer from "app/containers/VideoProgressContainer";
import VideoProgressRangeContainer from "app/containers/VideoProgressRangeContainer";
import VideoTitleEditor from "app/components/VideoTitleEditor/VideoTitleEditor";
import TimelineCard from "app/components/TimelineCard/TimelineCard";
import TimelineCardPositioner from "app/components/TimelineCardPositioner/TimelineCardPositioner";

import MouseHelper from "app/util/MouseHelper";
import ImageHelper from "app/util/ImageHelper";
import { states } from "timelinely-player/TimelinelyPlayer";

import "./WatchView.scss";
import VideoArrows from "app/components/VideoArrows/VideoArrows";
import MediaRecorderContainer from "app/containers/MediaRecorderContainer";
import VideoAboutContainer from "app/containers/VideoAboutContainer";

type Props = {
    video: any[],
    history: any[],
    items: any[],
    startTime: ?number,
    relatedTimelines: Video[],
    serverTime: any,
    embed: boolean,
    isScreenshotBot: boolean
};

type State = {
    videoHovering: boolean,
    isMouseStopped: boolean
};

const watchTourCookie = "watch-tour-cookie";

@observer
export default class WatchView extends React.Component<Props, State> {
    mouseStopTimer: TimeoutID;
    timelineWrapper: ?HTMLDivElement;

    constructor(props: Props) {
        super(props);

        this.state = { videoHovering: false, isMouseStopped: false };

        const video = Video.make(this.props.video);
        if (!video) throw new Error("Video must not be null/undefined");

        VideoStore.setVideo(video);
        VideoStore.setHistory(Video.makeArray(this.props.history));
        VideoStore.setRelatedTimelines(
            Video.makeArray(this.props.relatedTimelines)
        );
        TimelineItemStore.setItems(TimelineItem.makeArray(this.props.items));

        MouseHelper.init({
            onMove: this.handleOnMouseMoveScreen,
            onStop: this.handleOnMouseStopScreen
        });

        if (
            Cookies.get("publish-message") &&
            !Cookies.get("publish-message-registered")
        ) {
            Cookies.remove("publish-message");
            Cookies.set("publish-message-registered", true);
            AppearanceStore.notifySuccess(
                "Congrats on your first video! You're awesome."
            );
        }

        if (!!Cookies.get("share-video")) {
            VideoShareStore.share();
            Cookies.remove("share-video");
        }

        if (TimelineItemStore.items.length) {
            Cookies.set("share-video");
        }
    }

    componentDidMount() {
        if (this.props.startTime) VideoPlayerStore.seekTo(this.props.startTime);
    }

    getProgressFromMouseEvent(event: SyntheticMouseEvent<>) {
        const element = this.timelineWrapper;
        if (!element) return null;

        const distance = event.pageX - element.getBoundingClientRect().left;
        const duration = VideoStore.video.duration;

        return (duration * distance) / element.offsetWidth;
    }

    handleMouseMove = (event: SyntheticMouseEvent<>) => {
        const progress = this.getProgressFromMouseEvent(event);

        if (progress) {
            VideoEditorStore.setHoverProgress(progress);
        }
    };

    handleMouseOut = () => {
        VideoEditorStore.setHoverProgress(null);
    };

    handleMouseEnterVideo = () => {
        if (this.state.videoHovering) return;

        this.setState({ videoHovering: true });
    };

    handleMouseLeaveVideo = () => {
        if (!this.state.videoHovering) return;

        this.setState({ videoHovering: false });
    };

    handleDeselectVideo = () => {
        TimelineItemStore.deselect();
    };

    handleOnMouseMoveScreen = (event: SyntheticMouseEvent<>) => {
        if (this.timelineWrapper) {
            const timelineBounds = this.timelineWrapper.getBoundingClientRect();
            const x: number = event.pageX;
            const y: number = event.pageY;

            if (MouseHelper.isWithinBounds(x, y, timelineBounds)) {
                this.handleMouseMove(event);
            }
        }

        if (!this.state.isMouseStopped) return;
        this.setState({ isMouseStopped: false });
    };

    handleOnMouseStopScreen = () => {
        if (this.state.isMouseStopped) return;

        if (this.mouseStopTimer) {
            clearTimeout(this.mouseStopTimer);
        }

        this.mouseStopTimer = setTimeout(() => {
            this.setState({ isMouseStopped: true });
        }, 3000);
    };

    leftAnimationStyle () {
        return TimelineItemStore.selectedItem
            && TimelineItemStore.isExpanded
            && !AppearanceStore.isMobile
            && !VideoPlayerStore.watchTourEnabled
            ? { width: '50%'}
            : {}
    }

    rightAnimationStyle () {
        return TimelineItemStore.selectedItem
            && TimelineItemStore.isExpanded
            && !AppearanceStore.isMobile
            && !VideoPlayerStore.watchTourEnabled
            ? { width: '50%', marginLeft: '30px' }
            : {}
    }

    render() {
        const classes = ["WatchView"];
        let wizardMoveDetection = {};
        let wizardLeaveDetection = {};
        let mouseHoverDetection = {
            onMouseEnter: this.handleMouseEnterVideo,
            onMouseLeave: this.handleMouseLeaveVideo
        };

        if (AppearanceStore.embed) classes.push("WatchView--embed");

        if (AppearanceStore.isMobile) classes.push("WatchView--mobile");

        if (VideoStore.isEditable) {
            classes.push("WatchView--editable");
            wizardMoveDetection = { onMouseMove: this.handleMouseMove };
            wizardLeaveDetection = { onMouseLeave: this.handleMouseOut };
        }

        if (TimelineItemStore.wizard) {
            classes.push("WatchView--wizard");
        }

        if (this.shouldShowWatchTour()) {
            VideoPlayerStore.setWatchTour(true);
            Cookies.set(watchTourCookie, true);
            if (UserStore.user) {
                UserStore.user.watch_tour = true;
                UserService.setWatchTour();
            }
        }

        const videoAndProgressClasses = [
            "WatchView__videoAndProgress-relative"
        ];
        if (this.showPlayerMetadata) {
            videoAndProgressClasses.push(
                "WatchView__videoAndProgress-relative--big"
            );
        }

        let video = VideoStore.video;
        let thumbnail;
        if (video.thumbnail) {
            thumbnail = ImageHelper.makeUrl(video.thumbnail, "jpg", {
                width: 400
            });
        } else {
            thumbnail = video.source_thumbnail;
        }

        return (
            <div className={classes.join(" ")}>
                <div className="WatchView__background" style={{ backgroundImage: `url(${thumbnail})` }}></div>
                <WatchTour enabled={VideoPlayerStore.watchTourEnabled}>
                    <MediaRecorderContainer />
                    <div className="container-fluid WatchView__content-container">
                        {this.mobileLandscapeNotSupported()}
                        <div className="WatchView__content">
                            <div
                                className="WatchView__left"
                                style={this.leftAnimationStyle()}
                                {...wizardLeaveDetection}
                            >
                                <div className="WatchView__verticalView__left">
                                    <h3>
                                        { video.title }
                                        <span> &nbsp; </span>
                                        { this.renderEditTitleButton() }
                                        {this.renderArrows(true)}
                                    </h3>
                                    <VideoAboutContainer></VideoAboutContainer>
                                </div>
                                <div className="WatchView__videoAndProgress">
                                    <div
                                        className={videoAndProgressClasses.join(
                                            " "
                                        )}
                                        {...mouseHoverDetection}
                                    >
                                        {this.renderArrows(false)}

                                        <div className="WatchView__video">
                                            {this.props.isScreenshotBot && (
                                                <Logo type="no-beta" />
                                            )}
                                            <VideoPlayerContainer showPlayerMetadata={this.showPlayerMetadata}/>
                                            <VideoEndingContainer />
                                            <VideoBeforePlayOverlayContainer />
                                        </div>

                                        <div className="WatchView__progressAndRange">
                                            <div {...wizardMoveDetection}>
                                                {this.renderProgress()}
                                                <VideoProgressRangeContainer />
                                            </div>

                                            <div {...wizardMoveDetection}>
                                                <TimelineBarsContainer />
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        className="WatchView__wizard"
                                        {...wizardMoveDetection}
                                    >
                                        <TimelineWizardContainer />
                                    </div>

                                </div>

                                <div
                                    ref={element =>
                                        (this.timelineWrapper = element)
                                    }
                                    className="WatchView__timeline"
                                >
                                    <TimelineContainer />
                                </div>

                                {this.renderVerticalViewBottom()}

                                <ScalerContainer />
                            </div>

                            {this.renderVerticalViewRight()}
                            {this.renderRight()}
                        </div>
                    </div>
                </WatchTour>

                {this.renderWizardPrompt()}
                <VideoShareContainer />
                <VideoTitleEditor />
            </div>
        );
    }

    renderEditTitleButton() {
        if (!VideoStore.isOwner || AppearanceStore.embed) return null;
        if (!VideoStore.video.isPublished) return null;

        return (
            <button className="btn btn-xs btn-primary WatchView__changeTitle" onClick={VideoTitleEditorStore.startEditing}>Change title</button>
        );
    }

    renderVerticalViewBottom() {
        if(AppearanceStore.embed || AppearanceStore.isMobile) {
            const userId: ?number = UserStore.user ? UserStore.user.rowid : null;
            const user: ?User = UserStore.user;
            let inner = TimelineItemStore.selectedItem ? (
                    <CSSTransition key={TimelineItemStore.selectedItem.key} timeout={300} classNames="TimelineCardPositioner--animation" appear>
                        <TimelineCardPositioner
                            video={VideoStore.video}
                            selectedItem={TimelineItemStore.selectedItem}
                            isExpanded={TimelineItemStore.isExpanded}
                        >
                            <TimelineCard
                                userId={userId}
                                user={user}
                                video={VideoStore.video}
                                isOwner={VideoStore.isOwner}
                                editable={VideoStore.isEditable}
                                onSeek={VideoPlayerStore.seekTo}
                                item={TimelineItemStore.selectedItem}
                                onSave={TimelineItemStore.save}
                                onRequestEdit={TimelineItemStore.requestEdit}
                                onCancelWizard={TimelineItemStore.requestWizardCancellation}
                                onChange={TimelineItemStore.enableWizardChanged}
                                wizard={TimelineItemStore.wizard}
                                onReact={TimelineItemStore.saveReaction}
                                onComment={TimelineItemStore.saveComment}
                                onDelete={TimelineItemStore.remove}
                                onDeleteComment={TimelineItemStore.deleteComment}
                                serverTime={this.props.serverTime}
                                scale={AppearanceStore.scale}
                                progress={VideoPlayerStore.progress}
                                isVerticalView={true}
                                onExpand={TimelineItemStore.expand}
                                isExpanded={TimelineItemStore.isExpanded}
                                onUnExpand={TimelineItemStore.unExpand}
                                onRequestPauseMainVideo={VideoPlayerStore.pause}
                            />
                        </TimelineCardPositioner>
                    </CSSTransition>
                ) : ( //This need to be here, otherwise fade-out doesn't work
                    <CSSTransition key="none" timeout={300} classNames="TimelineCardPositioner--animation"><div></div></CSSTransition>
                );

            return (
                <div className="WatchView__verticalView__bottom">
                    <SwitchTransition>
                        {inner}
                    </SwitchTransition>
                </div>
            );
        }
    }

    renderRight() {
        if(AppearanceStore.embed || AppearanceStore.isMobile) {
            return null;
        }

        return (
            <div className="WatchView__right" style={this.rightAnimationStyle()}>
                {this.renderEditableRightPanel()}

                <TimelineCardSwitcherContainer
                    serverTime={this.props.serverTime}
                />
            </div>
        );
    }

    renderVerticalViewRight() {
        if(!AppearanceStore.embed || AppearanceStore.isMobile) {
            return null;
        }

        return (
                <div className="WatchView__verticalView__right col-md-2" style={this.rightAnimationStyle()}>
                    {this.renderEditableRightPanel()}
                    <RelatedTimelinesContainer
                        serverTime={this.props.serverTime} />
                </div>
        );
    }

    renderProgress() {
        return (
            <VideoProgressContainer
                hide={AppearanceStore.scale !== -1 && !this.showPlayerMetadata}
            />
        );
    }

    renderArrows(isOnTitle = false) {
        if (AppearanceStore.embed) return null;
        if (isOnTitle && !AppearanceStore.isMobile) return null;

        const show = isOnTitle ? true : !AppearanceStore.isMobile && this.showPlayerMetadata;
        
        return (
            <VideoArrows
                show={show}
                isVideoPaused={VideoPlayerStore.state === states.PAUSED}
                canGoBackward={TimelineItemStore.canSeekToPreviousItem}
                canGoForward={TimelineItemStore.canSeekToNextItem}
                onForward={TimelineItemStore.seekNext}
                onBackward={TimelineItemStore.seekPrevious}
                progress={VideoPlayerStore.progress}
            />
        );
    }

    renderEditableRightPanel() {
        if (!VideoStore.isOwner || AppearanceStore.embed) return;

        return (
            <section className="WatchView__right-edit">
                <VideoStatusContainer />
            </section>
        );
    }

    renderWizardPrompt() {
        if (!TimelineItemStore.waitForCancellationPrompt) {
            return null;
        }

        return (
            <ConfirmationModal
                message="You haven't saved your changes yet. Are you sure you want to leave?"
                actionText="Exit card editor"
                onConfirm={TimelineItemStore.cancelWizard}
                onCancel={TimelineItemStore.resumeWizardCancellation}
            />
        );
    }

    get showPlayerMetadata(): boolean {

        if (VideoPlayerStore.hasEnded) return false;
        if (!VideoPlayerStore.played) return false;
        
        return this.state.videoHovering && !this.state.isMouseStopped;
    }

    shouldShowWatchTour(): boolean {
        if (AppearanceStore.embed || !VideoStore.isEditable) {
            return false;
        }

        // if signed in user and never watched
        if (UserStore.user) {
            return !UserStore.user.watch_tour;
        }

        // if anonymous user, let's rely on cookies
        return !Cookies.get(watchTourCookie);
    }

    mobileLandscapeNotSupported() {
        if (AppearanceStore.embed || !AppearanceStore.isMobileBrowser) return null;

        return (
            <div className="WatchView__mobileLandscapeNotSupported alert alert-warning">
                Timelinely doesn't support landscape mode yet
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/app/views/app/video/Watch/WatchView.js